import {
  GravityFormData,
  GravityFormState,
  GravityFormsFieldState,
  useGravityForm,
} from "eddev/integrations/gravityforms"
import { useEffect, useMemo, useRef } from "react"
import { FIELD_TYPES } from "./gf-field-types"
import { Button } from "@components/atoms/Button"

type Props = {
  form: GravityFormData
  onSubmitted?: (data: GravityFormData) => void
  onChange?: (data: GravityFormState) => void
  dark?: boolean
  paginated?: boolean
}

type FieldGroupItem = {
  field: GravityFormsFieldState
  columns: number
}

function evaluateRule(fieldValue: any, operator: string, targetValue: string): boolean {
  switch (operator) {
    case "is":
      return fieldValue === targetValue
    case "isnot":
      return fieldValue !== targetValue
    case ">":
      return parseFloat(fieldValue) > parseFloat(targetValue)
    case "<":
      return parseFloat(fieldValue) < parseFloat(targetValue)
    case "contains":
      return typeof fieldValue === "string" && fieldValue.includes(targetValue)
    case "starts_with":
      return typeof fieldValue === "string" && fieldValue.startsWith(targetValue)
    case "ends_with":
      return typeof fieldValue === "string" && fieldValue.endsWith(targetValue)
    default:
      return false
  }
}

function evaluateConditionalLogic(
  logic: GravityFormsFieldState["conditionalLogic"],
  getValue: (fieldId: string | number) => any,
): boolean {
  if (!logic?.enabled || !logic.rules?.length) return true

  const results = logic.rules.map((rule: any) => {
    const value = getValue(rule.fieldId)
    return evaluateRule(value, rule.operator, rule.value)
  })

  const result = logic.logicType === "all" ? results.every(Boolean) : results.some(Boolean)

  return logic.actionType === "show" ? result : !result
}

export function GravityForm(props: Props) {
  const state = useGravityForm({
    form: props.form,
  })

  const fieldRows = useMemo(() => {
    const rows: FieldGroupItem[][] = []

    let lastGroupID = ""
    for (const field of state.fields) {
      if (field.type === "hidden" || field.visibility === "hidden") continue
      if (field.layoutGroupId !== lastGroupID) {
        rows.push([])
        lastGroupID = field.layoutGroupId
      }

      rows[rows.length - 1].push({
        field,
        columns: field.layoutGridColumnSpan ?? 1,
      })
    }

    return rows
  }, [state])

  useEffect(() => {
    if (state.complete && state.successMessage?.startsWith("__redirect__")) {
      window.location.href = state.successMessage.replace("__redirect__", "")
    }
  }, [state.complete])

  return (
    <>
      {state.complete && !state.successMessage?.startsWith("__redirect__") ? (
        <div
          className="type-title-s text-text text-center"
          dangerouslySetInnerHTML={{ __html: state.successMessage ?? "Thank you for submitting the form." }}
        />
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (state.submitting) return

            // Basic validation step (optional for now — already handled)
            const allValid = state.fields.every((field) =>
              typeof field.validate === "function" ? field.validate() : true,
            )

            if (!allValid) return
            state.submit()
          }}
          autoComplete="off"
          className="w-full flex flex-col gap-6"
        >
          <main className="flex flex-col gap-6 w-full">
            {fieldRows.map((row, i) => {
              const totalColumns = row.reduce((sum, { columns }) => sum + columns, 0)

              return (
                <div key={i} className="form-row">
                  {row.map(({ field, columns }, i) => {
                    const Component = (FIELD_TYPES as any)[field.type]
                    if (!Component) {
                      console.info("Unknown field type", field)
                      return null
                    }

                    const shouldShow = evaluateConditionalLogic(field.conditionalLogic, (fieldId) => {
                      const match = state.fields.find((f) => f.id === Number(fieldId))
                      return match?.value
                    })

                    if (!shouldShow) return null

                    return (
                      <div
                        key={i}
                        className="form-row-item"
                        style={
                          {
                            "--field-columns": columns / totalColumns,
                          } as any
                        }
                      >
                        <Component {...field} disabled={field.disabled || state.submitting} />
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </main>

          <footer className="flex gap-5 items-center justify-end">
            <Button size="m" style="primary" iconRight="ARROW_RIGHT" type="submit" disabled={state.submitting}>
              {props.form.button?.text === ""
                ? "Submit"
                : props.form.button.text === "&nbsp;"
                  ? ""
                  : props.form.button.text}
            </Button>
          </footer>
        </form>
      )}
    </>
  )
}
